/**
 * Rate a finished session.
 *
 * Ported from the app's `widgets/rate_session.dart`.
 *
 * *** THE STARS ARE NOT THE WHOLE ANSWER ***
 * The server also takes "did the mentor join on time" and "was your problem
 * resolved" — two facts an institute can act on, where a star average tells
 * them only that something was wrong. Both are optional, so a student who
 * wants to give five stars and leave can.
 *
 * *** ONCE ONLY ***
 * The server refuses a second rating. The list hides the button once
 * `my_rating` is set, so this dialog should never open on a rated session.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useRateSession } from '@/lib/queries'
import { formatDateTime } from '@/lib/format'
import type { MyBooking } from '@/types/api'
import styles from './SessionsPage.module.css'

/** What each star means, so the number is not left to interpretation. */
const WORDS = ['', 'Poor', 'Not great', 'Fine', 'Good', 'Excellent']

export function RateSessionDialog({
  booking,
  onClose,
}: {
  booking: MyBooking
  onClose: () => void
}) {
  const toast = useToast()
  const rate = useRateSession()

  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [onTime, setOnTime] = useState<boolean | null>(null)
  const [resolved, setResolved] = useState<boolean | null>(null)
  const [comment, setComment] = useState('')

  const submit = async () => {
    if (rating < 1) return
    try {
      await rate.mutateAsync({
        bookingId: booking.booking_id,
        rating,
        mentor_joined_on_time: onTime,
        problem_resolved: resolved,
        comment: comment.trim() || null,
      })
      toast.success('Thank you for the feedback.')
      onClose()
    } catch (caught) {
      /*
        409 here means it was already rated — worth saying plainly rather
        than as a generic failure, because the student has not done
        anything wrong.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not save your rating.',
      )
    }
  }

  /* Hovering previews; once set, the chosen value stands. */
  const shown = hover || rating

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.dialog}>
        <h2 className={styles.dialogTitle}>How was your session?</h2>
        <p className={styles.dialogSub}>
          {booking.mentor_name ?? 'Your mentor'} ·{' '}
          {formatDateTime(booking.slot_start_utc)}
        </p>

        <div
          className={styles.stars}
          role="radiogroup"
          aria-label="Rating out of five"
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={`${n} ${n === 1 ? 'star' : 'stars'} — ${WORDS[n]}`}
              className={`${styles.star} ${n <= shown ? styles.starOn : ''}`}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
            >
              ★
            </button>
          ))}
          {/* Reserved height, so choosing a star does not shift the dialog. */}
          <span className={styles.starWord}>{shown ? WORDS[shown] : ' '}</span>
        </div>

        {/*
          Both optional. A student who gives five stars and closes has said
          enough; these exist for the ones who want to be specific.
        */}
        <div className={styles.askRow}>
          <span className={styles.askLabel}>Did they join on time?</span>
          <div className={styles.askButtons}>
            <button
              type="button"
              className={`${styles.ask} ${onTime === true ? styles.askOn : ''}`}
              onClick={() => setOnTime(onTime === true ? null : true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.ask} ${onTime === false ? styles.askOn : ''}`}
              onClick={() => setOnTime(onTime === false ? null : false)}
            >
              No
            </button>
          </div>
        </div>

        <div className={styles.askRow}>
          <span className={styles.askLabel}>Was your problem resolved?</span>
          <div className={styles.askButtons}>
            <button
              type="button"
              className={`${styles.ask} ${resolved === true ? styles.askOn : ''}`}
              onClick={() => setResolved(resolved === true ? null : true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.ask} ${resolved === false ? styles.askOn : ''}`}
              onClick={() => setResolved(resolved === false ? null : false)}
            >
              No
            </button>
          </div>
        </div>

        <textarea
          className={styles.comment}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Anything else? (optional)"
          maxLength={2000}
          rows={3}
        />

        <div className={styles.dialogActions}>
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            /* A rating is the one required field. */
            disabled={rating < 1}
            loading={rate.isPending}
          >
            Send feedback
          </Button>
        </div>
      </div>
    </div>
  )
}
